<template>
    <span class="account-selector-container">
        <div v-if="showDepartments && filteredDepartments.length" class="departments-container">
            <div class="departments-list">
                <div v-for="department in filteredDepartments"
                     :key="department.id"
                     @click="selectDepartment(department)"
                     :class="{ 'activeDepartment': department.id === account.department.id }"
                     class="department-item"
                >
                    <span class="item-name">{{ department.name }}</span>
                    <span class="item-id"
                          :class="{ 'item-id--head-office': department.is_head_office }"
                          :title="department.is_head_office ? 'Hovedkontor' : ''"
                    >{{ department.id }}</span>
                </div>
            </div>
        </div>

        <div v-if="recentPanelVisible" class="recent-container">
            <div class="departments-list">
                <div v-for="entry in visibleRecentDepartments"
                     :key="entry.department_id"
                     @click="selectRecent(entry)"
                     :class="{ 'activeDepartment': entry.department_id === account.department.id }"
                     class="department-item"
                >
                    <span class="item-name">
                        {{ entry.department_name }}
                        <small class="item-sub">{{ entry.customer_name }}</small>
                    </span>
                    <span class="item-id">{{ entry.department_id }}</span>
                </div>
            </div>
        </div>

        <el-dropdown class="item dropdown-min-width" trigger="click" ref="customerDropdown"
                     :hide-on-click="false" @visible-change="handleDropdownVisibleChange">
            <span class="el-dropdown-link" style="color: #ffffff; cursor: pointer;">
                <span v-if="active_account && active_account.hasOwnProperty('department')">
                    {{ active_account.department.title }}
                </span>
                <i class="el-icon-arrow-down el-icon--right"></i>
            </span>

            <el-dropdown-menu slot="dropdown">
              <div class="search-container">
                <el-input
                    ref="searchInput"
                    placeholder="Søk etter kunde, butikk eller ID.."
                    v-model="query"
                    @click.native.stop=""
                    size="small"
                >
                </el-input>

                <div class="search-toolbar">
                  <div class="hidden-toggle" @click.stop="toggleHiddenList">
                    <i :class="showHiddenOnly ? 'el-icon-back' : 'el-icon-view'"></i>
                    <span v-if="showHiddenOnly">Tilbake til kunder</span>
                    <span v-else>Skjulte ({{ hiddenCustomerIds.length }})</span>
                  </div>

                  <span class="toolbar-checkboxes">
                    <el-checkbox
                        v-model="showDepartmentsOnOpen"
                        class="departments-toggle"
                        @click.native.stop
                        @change="onShowDepartmentsOnOpenChange"
                    >
                      Vis avdelinger
                    </el-checkbox>

                    <el-checkbox
                        v-model="showRecentPanel"
                        class="departments-toggle"
                        @click.native.stop
                        @change="onShowRecentChange"
                    >
                      Siste brukte
                    </el-checkbox>
                  </span>
                </div>
              </div>

                <div class="customers-scroll-container">
                    <div v-for="customer in filteredCustomers"
                         :key="customer.id"
                         :class="{
                             'activeDepartment': customer.id === selectedCustomer.id,
                             'dragging': dragCustomerId === customer.id
                         }"
                         class="customer-item"
                         :data-customer-id="customer.id"
                         @mousedown="onPressStart(customer, $event)"
                         @click="onCustomerClick(customer)"
                    >
                        <span class="item-name">{{ customer.name }}</span>
                        <span class="item-right">
                            <span class="item-id">{{ customer.id }}</span>
                            <i v-if="showHiddenOnly"
                               class="el-icon-refresh-left customer-action customer-action--restore"
                               title="Vis kunde igjen"
                               @click.stop="restoreCustomer(customer)"
                            ></i>
                            <i v-else
                               class="el-icon-close customer-action customer-action--hide"
                               title="Skjul kunde"
                               @click.stop="hideCustomer(customer)"
                            ></i>
                        </span>
                    </div>

                    <div v-if="showHiddenOnly && !filteredCustomers.length" class="customers-empty">
                        Ingen skjulte kunder
                    </div>
                </div>
            </el-dropdown-menu>
        </el-dropdown>
    </span>
</template>

<script>
import Axios from 'axios';
import handleCookies from '@/mixins/handle-cookies.js';
import Cookies from "js-cookie";

export default {
  props: ['active_account'],

  mixins: [handleCookies],

  data() {
    return {
      search_threshold: 10,
      accounts: [],
      departments: [],
      query: '',
      customer_query: '',
      account: JSON.parse(localStorage.getItem('active_account')),
      selected_account: {},
      showDepartments: false,
      selectedCustomer: {},
      departmentsLoaded: false,
      customerDepartments: [],
      settings_url: 'https://api.brandsafe.no/v1/config/user-settings',
      hidden_customers_key: 'account_selector_hidden_customers',
      customers_order_key: 'account_selector_customer_order',
      show_departments_key: 'account_selector_show_departments',
      showDepartmentsOnOpen: false,
      recent_departments_key: 'account_selector_recent_departments',
      recent_limit: 7,
      recentDepartments: [],
      show_recent_key: 'account_selector_show_recent',
      showRecentPanel: false,
      dropdownOpen: false,
      customerOrder: [],
      dragCustomerId: null,
      longPressTimer: null,
      long_press_delay: 600,
      pressPosition: null,
      suppressClick: false,
      hiddenCustomerIds: [],
      showHiddenOnly: false
    }
  },

  created() {
    this.loadSettings();
    this.getAccounts();
    this.selected_account = this.active_account;
    this.selectedCustomer = {id: this.account.customer.id};
  },

  watch: {
    async query(value) {
      if (this.showHiddenOnly) {
        return;
      }

      if (!value.trim()) {
        this.showDepartments = this.showDepartmentsOnOpen;
        return;
      }

      await this.openCurrentDepartments();
    }
  },

  computed: {
    visibleRecentDepartments() {
      return this.recentDepartments.slice(0, this.recent_limit);
    },

    recentPanelVisible() {
      return this.dropdownOpen && this.showRecentPanel && this.recentDepartments.length > 0;
    },

    canReorder() {
      return !this.showHiddenOnly && !this.query.trim();
    },

    orderedAccounts() {
      if (!this.customerOrder.length) {
        return this.accounts;
      }

      return this.accounts.slice().sort((a, b) => {
        const a_index = this.customerOrder.indexOf(a.id);
        const b_index = this.customerOrder.indexOf(b.id);

        if (a_index === -1 && b_index === -1) return 0;
        if (a_index === -1) return 1;
        if (b_index === -1) return -1;

        return a_index - b_index;
      });
    },

    filteredCustomers() {
      return this.orderedAccounts.filter(customer => {
        const is_hidden = this.hiddenCustomerIds.includes(customer.id);

        if (this.showHiddenOnly ? !is_hidden : is_hidden) {
          return false;
        }

        return this.matchesQuery(customer, this.query);
      });
    },

    filteredDepartments() {
      return this.departments.filter(department => {
        return this.matchesQuery(department, this.query);
      });
    }
  },

  beforeDestroy() {
    document.removeEventListener('mousemove', this.onPointerMove);
    document.removeEventListener('mouseup', this.onPointerUp);
  },

  methods: {
    async getDepartments(customer) {
      let token = this.$cookies.get('bs_token');
      await Axios.get(`https://api.brandsafe.no/v1/config/all-departments/${customer.id}`, {headers: {'Authorization': 'Bearer ' + token}}).then((response) => {
        this.departments = response.data;
        this.departmentsLoaded = true;
      }, (error) => {
        this.departments = [];
        this.departmentsLoaded = false;
      });
    },

    getAccounts() {
      let token = this.$cookies.get('bs_token');
      Axios.get('https://api.brandsafe.no/v1/config/sorted-customers', {headers: {'Authorization': 'Bearer ' + token}}).then((response) => {
        this.accounts = response.data;

        const current = this.accounts.find(customer => customer.id === this.selectedCustomer.id);

        if (current) {
          this.selectedCustomer = current;
        }
      }, (error) => {
        this.accounts = [];
      });
    },

    handleDropdownVisibleChange(visible) {
      this.dropdownOpen = visible;

      if (!visible) {
        this.showDepartments = false;
        this.showHiddenOnly = false;
        this.query = '';
        return;
      }

      this.focusSearch();

      if (this.showDepartmentsOnOpen) {
        this.openCurrentDepartments();
      }
    },

    focusSearch() {
      this.$nextTick(() => {
        const input = this.$refs.searchInput;

        if (input) {
          input.focus();
        }
      });
    },

    rememberRecentDepartment(department) {
      const customer = this.accounts.find(item => item.id === this.selectedCustomer.id);

      const entry = {
        department_id: department.id,
        department_name: department.name,
        customer_id: this.selectedCustomer.id,
        customer_name: this.selectedCustomer.name || (customer ? customer.name : '')
      };

      const rest = this.recentDepartments.filter(item => item.department_id !== entry.department_id);

      this.recentDepartments = [entry].concat(rest).slice(0, this.recent_limit);

      return this.saveSetting(this.recent_departments_key, this.recentDepartments);
    },

    async openCurrentDepartments() {
      if (!this.departmentsLoaded) {
        await this.getDepartments(this.selectedCustomer);
      }

      this.showDepartments = true;
    },

    onShowRecentChange(value) {
      this.saveSetting(this.show_recent_key, value);
    },

    async selectRecent(entry) {
      const customer = this.accounts.find(item => item.id === entry.customer_id);

      if (customer) {
        this.selectedCustomer = customer;
      }

      await this.selectDepartment({id: entry.department_id, name: entry.department_name});
    },

    onShowDepartmentsOnOpenChange(value) {
      this.saveSetting(this.show_departments_key, value);

      if (value) {
        this.openCurrentDepartments();
      } else if (!this.query.trim()) {
        this.showDepartments = false;
      }
    },

    matchesQuery(item, query) {
      const needle = (query || '').trim().toLowerCase();

      if (!needle) {
        return true;
      }

      return (item.name || '').toLowerCase().includes(needle)
          || String(item.id).includes(needle);
    },

    async loadSettings() {
      const token = this.$cookies.get('bs_token');

      try {
        const response = await Axios.get(this.settings_url, {headers: {'Authorization': 'Bearer ' + token}});
        const settings = response.data.data || {};

        this.hiddenCustomerIds = this.asArray(settings[this.hidden_customers_key]);
        this.customerOrder = this.asArray(settings[this.customers_order_key]);
        this.recentDepartments = this.asArray(settings[this.recent_departments_key]);
        this.showDepartmentsOnOpen = settings[this.show_departments_key] === true;
        this.showRecentPanel = settings[this.show_recent_key] === true;
      } catch (error) {
        console.log('Could not load user settings:', error);
      }
    },

    asArray(value) {
      return Array.isArray(value) ? value : [];
    },

    saveSetting(key, value) {
      const token = this.$cookies.get('bs_token');

      return Axios.put(
          `${this.settings_url}/${key}`,
          {value: value},
          {headers: {'Authorization': 'Bearer ' + token}}
      ).catch((error) => {
        console.log('Could not save user setting ' + key + ':', error);
      });
    },

    saveCustomerOrder() {
      this.saveSetting(this.customers_order_key, this.customerOrder);
    },

    onPressStart(customer, event) {
      if (!this.canReorder || event.button !== 0) {
        return;
      }

      this.pressPosition = {x: event.clientX, y: event.clientY};

      document.addEventListener('mousemove', this.onPointerMove);
      document.addEventListener('mouseup', this.onPointerUp);

      this.longPressTimer = setTimeout(() => {
        this.dragCustomerId = customer.id;
        this.suppressClick = true;
      }, this.long_press_delay);
    },

    onPointerMove(event) {
      // moving before the long press fired means the user is clicking/scrolling, not reordering
      if (!this.dragCustomerId) {
        const moved = Math.abs(event.clientX - this.pressPosition.x)
            + Math.abs(event.clientY - this.pressPosition.y);

        if (moved > 5) {
          clearTimeout(this.longPressTimer);
          this.longPressTimer = null;
        }

        return;
      }

      event.preventDefault();

      const target_id = this.customerIdAtPoint(event.clientX, event.clientY);

      if (target_id !== null) {
        this.moveCustomer(this.dragCustomerId, target_id);
      }

      this.autoScroll(event.clientY);
    },

    onPointerUp() {
      document.removeEventListener('mousemove', this.onPointerMove);
      document.removeEventListener('mouseup', this.onPointerUp);

      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;

      if (this.dragCustomerId) {
        this.saveCustomerOrder();
      }

      this.dragCustomerId = null;
      this.pressPosition = null;

      // the click event fires right after mouseup, so drop the guard once it is past
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    },

    customerIdAtPoint(x, y) {
      const element = document.elementFromPoint(x, y);
      const item = element && element.closest ? element.closest('.customer-item') : null;

      if (!item || !item.dataset.customerId) {
        return null;
      }

      return Number(item.dataset.customerId);
    },

    autoScroll(y) {
      const container = this.$el.querySelector('.customers-scroll-container');

      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const edge = 30;

      if (y < rect.top + edge) {
        container.scrollTop -= 10;
      } else if (y > rect.bottom - edge) {
        container.scrollTop += 10;
      }
    },

    onCustomerClick(customer) {
      if (this.suppressClick) {
        return;
      }

      if (this.showHiddenOnly) {
        this.restoreCustomer(customer);
        return;
      }

      this.selectCustomer(customer);
    },

    moveCustomer(fromId, toId) {
      if (fromId === null || fromId === toId) {
        return;
      }

      const ids = this.orderedAccounts.map(customer => customer.id);
      const from = ids.indexOf(fromId);
      const to = ids.indexOf(toId);

      if (from === -1 || to === -1) {
        return;
      }

      ids.splice(to, 0, ids.splice(from, 1)[0]);

      this.customerOrder = ids;
    },

    saveHiddenCustomers() {
      this.saveSetting(this.hidden_customers_key, this.hiddenCustomerIds);
    },

    hideCustomer(customer) {
      if (!this.hiddenCustomerIds.includes(customer.id)) {
        this.hiddenCustomerIds.push(customer.id);
        this.saveHiddenCustomers();
      }

      this.showDepartments = false;
    },

    restoreCustomer(customer) {
      this.hiddenCustomerIds = this.hiddenCustomerIds.filter(id => id !== customer.id);
      this.saveHiddenCustomers();
    },

    toggleHiddenList() {
      this.showHiddenOnly = !this.showHiddenOnly;
      this.showDepartments = false;
      this.query = '';
    },

    async selectCustomer(customer) {
      await this.getDepartments(customer)
      this.selectedCustomer = customer;

      if (customer.name === "Brandsafe") {
        const brandsafe = this.departments.find(dept => dept.id === 2927);
        await this.selectDepartment(brandsafe)
      } else {
        this.showDepartments = true;
      }
    },

    async createUserAccountForDepartment(userId, account) {
      const token = this.$cookies.get('bs_token');

      const accountData = {
        customer_id: this.selectedCustomer.id,
        jwt_id: 2,
        department_id: account.department_id || account.id,
        language: this.selectedCustomer.language || this.account.language,
        profile: this.selectedCustomer.profile || this.account.profile
      };

      try {
        const response = await Axios.post(
            `https://api.brandsafe.no/v1/config/single-user/${userId}/accounts`,
            accountData,
            {headers: {'Authorization': 'Bearer ' + token}}
        );
        return response.data.account;
      } catch (error) {
        console.log('Account may already exist or could not be created:', error);
        return null;
      }
    },

    async changeUserActiveAccount(accountId) {
      const token = this.$cookies.get('bs_token');

      const response = await Axios.post(
          'https://api.brandsafe.no/v1/profile/accounts',
          {id: accountId},
          {headers: {'Authorization': 'Bearer ' + token}}
      );

      return response.data.data;
    },

    async saveAccountCookies(accountData) {
      const cookies = [
        {key: 'bs_language', value: accountData.language},
        {key: 'bs_role', value: accountData.role.title},
        {key: 'bs_profile', value: accountData.profile},
        {key: 'bs_active_department_id', value: accountData.department.id},
        {key: 'bs_active_customer_id', value: accountData.customer.id}
      ];

      await this.setCookies(cookies);
    },

    async selectDepartment(department) {
      if (department.id === this.active_account.id) {
        this.showDepartments = false;
        return false;
      }

      try {
        const userId = this.$cookies.get('bs_user_id')

        const account = await this.createUserAccountForDepartment(userId, department);

        const accountData = await this.changeUserActiveAccount(account.uuid);

        await this.saveAccountCookies(accountData);

        await this.rememberRecentDepartment(department);

        this.showDepartments = false;

        this.$nextTick(() => {
          location.reload();
        });
      } catch (error) {
        console.error('Error while changing department:', error);
        alert('could_not_change_department');
        this.showDepartments = false;
      }
    },
  }
}
</script>

<style lang="less" scoped>
.account-selector-container {
  position: relative;
  display: inline-block;
}

.dropdown-min-width {
  min-width: 200px;
}

.search-container {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  padding: 10px;
  border-bottom: 1px solid #ebeef5;
}

.el-dropdown-menu {
  max-height: none !important;
  width: 300px !important;
  min-width: 300px !important;
  padding: 0 !important;
  margin-left: -30px !important;
}

.customers-scroll-container {
  max-height: 70vh;
  min-height: 150px;
  overflow-y: auto;
}

.departments-container {
  position: absolute;
  top: 90px;
  right: 315px;
  width: 250px;
  max-height: 800px;
  min-height: 100px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.2);
  z-index: 2001;
  margin-right: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.departments-header {
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
  font-weight: bold;
  color: #333;
}

.departments-header i {
  cursor: pointer;
  color: #909399;
}

.department-search {
  padding: 10px;
}

.departments-list {
  overflow-y: auto;
  max-height: 500px;
  flex: 1;
}

.department-item {
  padding: 8px 15px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.3s;
  color: #333;
  font-size: 16px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.department-item:hover {
  background-color: #eceef1;
}

.customer-item {
  list-style: none;
  line-height: 40px;
  padding: 0 20px;
  margin: 0;
  font-size: 16px;
  color: #404244;
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  user-select: none;
}

.item-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 12px;
}

.item-id {
  font-size: 11px;
  color: #a8abb2;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.item-id--head-office {
  color: #409eff;
  font-weight: 600;
}

.customer-action {
  visibility: hidden;
  color: #909399;
  font-size: 14px;
}

.customer-item.dragging {
  cursor: grabbing;
  background-color: #eceef1;
  box-shadow: inset 3px 0 0 0 #67c23a;
}

.customer-action--hide:hover {
  color: #f56c6c;
}

.customer-action--restore {
  visibility: visible;
}

.customer-action--restore:hover {
  color: #67c23a;
}

.customer-item:hover .customer-action {
  visibility: visible;
}

.customers-empty {
  padding: 20px;
  text-align: center;
  color: #909399;
  font-size: 14px;
}

.search-toolbar {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 10px;
}

.toolbar-checkboxes {
  display: flex;
  align-items: center;
  gap: 10px;
}

.recent-container {
  position: absolute;
  top: 90px;
  left: 100%;
  margin-left: -30px;
  width: 220px;
  max-height: 800px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.2);
  z-index: 2001;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.recent-container .department-item {
  padding: 8px 14px;
  font-size: 14px;
  color: #606266;
}

.recent-container .item-id {
  font-size: 11px;
}

.item-sub {
  display: block;
  font-size: 11px;
  color: #a8abb2;
  line-height: 14px;
}

.departments-toggle {
  margin-right: 0;
}

.departments-toggle /deep/ .el-checkbox__label {
  font-size: 12px;
  color: #909399;
  padding-left: 6px;
}

.hidden-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #909399;
  cursor: pointer;
  user-select: none;
}

.hidden-toggle:hover {
  color: #404244;
}

.customer-item:hover {
  background-color: #eceef1;
}

.customer-item.activeDepartment:hover {
  background-color: #e3f1c9;
}

.department-item.activeDepartment:hover {
  background-color: #e3f1c9;
}


</style>